import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { ChefHat } from "lucide-react";
import { LoadingComponent } from "../components/LoadingComponent";
import { EmptyState } from "../components/EmptyStateComponent";
import { ErrorModal } from "../components/modals/ErrorModal";
import { UserMealPageHeader } from "../components/UserMealPage/UserMealPageHeader";
import { UserMealGrid } from "../components/UserMealPage/UserMealGrid";
import { UserMealList } from "../components/UserMealPage/UserMealList";
import { WheatOff } from "lucide-react";
import { EmptyComponentWithAnimation } from "../components/ShoppingListPage/EmptyComponentWithAnimation";
import { useMeals } from "../contexts/UserMealsContext";
import { ConfirmActionModal } from "../components/modals/ConfirmActionModal";

export function UserMealsPage() {
  const navigate = useNavigate();
  const { meals, removeMeal } = useMeals();

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [mealsToDisplay, setMealsToDisplay] = useState(null);
  const [categories, setCategories] = useState(["All"]);
  const [deleteMealFailure, setDeleteMealFailure] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [mealToDelete, setMealToDelete] = useState(null);

  const removeButtonClicked = (mealId) => {
    setMealToDelete(mealId);
    setShowConfirmDeleteModal(true);
  };

  const handleRemoveMeal = async (mealId) => {
    setShowConfirmDeleteModal(false);
    setLoadingDelete(true);
    try {
      await removeMeal(mealId);
    } catch (error) {
      setDeleteMealFailure(error);
      // Error handling is now done in the context, so we just log here
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleEditMeal = (mealId) => {
    navigate(`/recipes/edit/${mealId}`);
  };

  useEffect(() => {
    if (meals !== null && meals !== undefined) {
      setLoading(false);
      setMealsToDisplay(meals);
    }
  }, [meals]);

  useEffect(() => {
    if (meals === null || meals === undefined) {
      return;
    }

    const uniqueCategories = [
      ...new Set(
        meals.flatMap((recipe) =>
          recipe.mealTagList.map((tag) => tag.tagContent),
        ),
      ),
    ];
    setCategories(["All", ...uniqueCategories]);

    const filteredRecipes = meals.filter((recipe) => {
      const mealTags = recipe.mealTagList.map((tag) => tag.tagContent);
      const matchesCategory =
        selectedCategory === "All" || mealTags.includes(selectedCategory);
      const matchesSearch =
        searchQuery === "" ||
        recipe.mealName.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    setMealsToDisplay(filteredRecipes);
  }, [searchQuery, meals, selectedCategory]);

  if (loading) {
    return <LoadingComponent message={"Loading your meals..."} />;
  }

  if (loadingDelete) {
    return <LoadingComponent message={"Deleting meal...."} />;
  }

  if (meals.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <EmptyState
          icon={ChefHat}
          title="No recipes yet"
          description="Start building your recipe collection! Create your first meal and begin planning delicious dishes for the week ahead."
          action={
            <button
              onClick={() => navigate("/CreateMeal")}
              className="px-6 py-3 bg-black text-white rounded-lg hover:opacity-90 transition-all duration-200"
            >
              Create Your First Recipe
            </button>
          }
        />
      </div>
    );
  }

  if (deleteMealFailure) {
    let errorMessage = "Failed to delete meal. Please try again.";
    const errorStatus = deleteMealFailure.response?.status;
    if (errorStatus === 409) {
      errorMessage =
        "This meal is currently being used in a the meal plan please remove it before deleting the meal.";
    }

    return (
      <div className="h-full flex items-center justify-center">
        <ErrorModal
          message={errorMessage}
          onClose={() => setDeleteMealFailure(null)}
          closeButtonText={"Got it"}
          headerText={"Delete Failed"}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <UserMealPageHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        onCreateMeal={() => navigate("/CreateMeal")}
      />

      {mealsToDisplay?.length === 0 ? (
        <EmptyComponentWithAnimation
          icon={WheatOff}
          title="No items found"
          description="Try search for a different term!"
          phrases={["Maybe brush up on your spelling..."]}
        />
      ) : (
        <>
          {/* Recipes List Mobile */}
          <div className="md:hidden flex-1 overflow-auto p-4 md:p-8">
            <UserMealList
              mealToDisplay={mealsToDisplay}
              handleRemoveMeal={removeButtonClicked}
              handleEdit={handleEditMeal}
            />
          </div>

          {/* Recipes Grid Desktop */}
          <div className="hidden md:block">
            <UserMealGrid
              mealToDisplay={mealsToDisplay}
              handleRemoveMeal={removeButtonClicked}
              handleEdit={handleEditMeal}
            />
          </div>
        </>
      )}
      {showConfirmDeleteModal && (
        <ConfirmActionModal
          action={() => handleRemoveMeal(mealToDelete)}
          closeModal={() => setShowConfirmDeleteModal(false)}
          heading="Havn't made this in months?"
          message="But are you sure you want to delete? This action cannot be undone."
          confirmButtonText="Delete Meal"
        />
      )}
    </div>
  );
}
